import { AnyAction } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch } from 'react-redux';

import {
  StatusAlertActionHandlers,
  StatusAlertActionId,
} from '@/components/common/StatusAlert';
import {
  ALERT_ANALYTICS_META,
  AlertId,
  EVENT_PEPPER_ONE_ALERTS,
  EventPepperOneAlertId,
  OPERATION_OUTCOME_MESSAGE,
  OperationOutcome,
  OperationOutcomeMessage,
  STATUS_ALERTS,
  StatusAlertId,
} from '@/components/common/StatusAlert/statusAlerts.config';
import ENVIRONMENT from '@/environments/environment';
import { InitialStatus } from '@/interfaces/OperationsDashboardInterface/OperationsDashboardInterface';
import { MonthSummary } from '@/interfaces/ReportingInterface/ReportingInterface';
import { Dashboard } from '@/redux/operation/operationsInterface';
import { refreshRecentMonthSummary } from '@/redux/reporting/reporting';
import {
  registerClickNotificationBannerCta,
  registerShowNotificationBanner,
} from '@/services/analytics';

interface UseAlertHandlersActionsParams {
  // Handlers de acciones de botón
  triggerNewOperation: () => void;
  selectOnlyStatus: (initials: InitialStatus) => void;
  // Navegación a los datos de contacto comercial (avisos por evento).
  goToContact: () => void;
  // Datos necesarios para los cálculos de prioridad
  monthSummaries: MonthSummary[];
  operations: Dashboard[];
  // indica que los datos ya están listos para evaluar
  ready?: boolean;
  //  testData?: boolean; // para activar datos de prueba y probar las distintas reglas
}

export interface AlertsFlags {
  isPdvInactive2M: boolean;
  isPdvInactive1M: boolean;
  isLowPacePrevMonth: boolean;
  isNearPacePrevMonth: boolean;
  // isCloseToRecord: boolean;
  isGoodPace: boolean;
  hasPendingApproved: boolean;
  hasPendingCapture: boolean;
}

export interface UseAlertHandlersActionsResult {
  // datos que se devuelven en el hook para que el componente de aviso pueda renderizarse
  handlers: StatusAlertActionHandlers;
  flags: AlertsFlags;
  // Aviso a mostrar. Si hay un aviso por evento activo gana siempre
  // si no, el métrico de mayor prioridad.
  // null si no aplica ninguno.
  activeAlertId: AlertId | null;
  // true si el aviso activo proviene de un evento de operación (Pepper One).
  isEventAlert: boolean;
  // Descarta el resultado de operación persistido (cierre del aviso por evento).
  clearOperationOutcome: () => void;
  // Importe de la operación que ha generado el aviso por evento (Pepper One).
  amount: number | null;
}

const PERCENT_80 = 0.8;
const PERCENT_50 = 0.5;

const OUTCOME_STORAGE_KEY = 'Pepper-OperationOutcome';
const AMOUNT_STORAGE_KEY = 'Pepper-OperationAmount';
// Identifica la operación a la que pertenecen los outcomes acumulados: si llega
// un proposalId distinto, es otra operación y se reinicia la acumulación.
const PROPOSAL_STORAGE_KEY = 'Pepper-OperationProposalId';

// Comprueba que el mensaje recibido por postMessage es un resultado válido (formato esperado).
const isOperationOutcomeMessage = (
  data: unknown,
): data is OperationOutcomeMessage => Boolean(
  data
  && typeof data === 'object'
  && (data as OperationOutcomeMessage).message === OPERATION_OUTCOME_MESSAGE
  && Object.values(OperationOutcome).includes(
    (data as OperationOutcomeMessage).outcome,
  ),
);

// Ordena los avisos por `priority` y devuelve el id de mayor
function resolveAlertByPriority<TId extends string>(
  catalog: Record<TId, { id: TId; priority: number }>,
  conditions: Record<TId, boolean>,
): TId | null {
  const winner = (
    Object.values(catalog) as Array<{ id: TId; priority: number }>
  )
    .sort((a, b) => a.priority - b.priority)
    .find(({ id }) => conditions[id]);
  return winner ? winner.id : null;
}

export function useAlertHandlersActions({
  triggerNewOperation,
  selectOnlyStatus,
  goToContact,
  monthSummaries,
  operations,
  ready = true,
  // testData = false,
}: UseAlertHandlersActionsParams): UseAlertHandlersActionsResult {
  const dispatch = useDispatch();
  // evento Pepper One postMessage
  // Pueden llegar VARIOS outcomes para la misma operación; se acumulan (sin
  // duplicar) y luego `resolveAlertByPriority` elige el de mayor prioridad.
  const [outcomes, setOutcomes] = useState<OperationOutcome[]>(() => {
    const stored = sessionStorage.getItem(OUTCOME_STORAGE_KEY);
    if (!stored) return [];
    try {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      return [stored as OperationOutcome];
    }
  });

  // Importe de la operación (Pepper One)
  const [amount, setAmount] = useState<number | null>(() => {
    const stored = sessionStorage.getItem(AMOUNT_STORAGE_KEY);
    return stored ? Number(stored) : null;
  });

  const isFreshOutcomeRef = useRef(false);
  // proposalId de la operación cuyos outcomes están acumulados.
  const proposalIdRef = useRef<string | null>(
    sessionStorage.getItem(PROPOSAL_STORAGE_KEY),
  );

  useEffect(() => {
    // escucha de mensajes postMessage desde Pepper One (resultado de operación)
    const listener = (event: MessageEvent) => {
      if (event.origin !== ENVIRONMENT.URL_PEPPERONE) return;
      if (!isOperationOutcomeMessage(event.data)) return;
      // Llega por postMessage, es un aviso nuevo, marcar como fresco.
      isFreshOutcomeRef.current = true;

      const received = event.data.outcome;
      const incomingProposalId = event.data.proposalId ?? null;

      // Si el proposalId es distinto al acumulado, es OTRA operación: se
      // reinicia la acumulación (outcomes e importe) para no mezclar avisos.
      const isNewOperation = incomingProposalId != null
        && proposalIdRef.current != null
        && incomingProposalId !== proposalIdRef.current;

      if (incomingProposalId != null) {
        proposalIdRef.current = incomingProposalId;
        sessionStorage.setItem(PROPOSAL_STORAGE_KEY, incomingProposalId);
      }

      // Nueva operación → empieza de cero con este outcome; si no, acumula.
      setOutcomes((prev) => {
        if (isNewOperation) return [received];
        return prev.includes(received) ? prev : [...prev, received];
      });

      if (isNewOperation) {
        setAmount(null);
        sessionStorage.removeItem(AMOUNT_STORAGE_KEY);
      }
      if (event.data.amount != null) {
        setAmount(event.data.amount);
        sessionStorage.setItem(AMOUNT_STORAGE_KEY, String(event.data.amount));
      }
    };
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, []);

  // guarda outcomes acumulados
  useEffect(() => {
    if (outcomes.length) {
      sessionStorage.setItem(OUTCOME_STORAGE_KEY, JSON.stringify(outcomes));
    }
  }, [outcomes]);

  const clearOperationOutcome = () => {
    isFreshOutcomeRef.current = false;
    proposalIdRef.current = null;
    setOutcomes([]);
    sessionStorage.removeItem(OUTCOME_STORAGE_KEY);
    setAmount(null);
    sessionStorage.removeItem(AMOUNT_STORAGE_KEY);
    sessionStorage.removeItem(PROPOSAL_STORAGE_KEY);
  };

  // Aviso por evento pepper one
  const eventAlertId = useMemo<EventPepperOneAlertId | null>(() => {
    // Hasta que los summaries no hayan cargado no se puede decidir la prioridad
    if (!ready) return null;
    if (!outcomes.length) return null;

    // evalúa los postMessages que llegan y si tiene
    const has = (o: OperationOutcome) => outcomes.includes(o);

    // monthSummaries viene ordenado: [0] mes actual, [1] anterior, …
    const current = /* testData ? monthSummaries[1] : */ monthSummaries[0];
    const previous = /* testData ? monthSummaries[2] : */ monthSummaries[1];

    const currentNumberOfLoans = current?.total_loans ?? 0;
    // Si no hay datos de meses anteriores, se asume que no hubo financiaciones (0).
    const previousNumberOfLoans = previous?.total_loans ?? 0;

    const isFirstOperation = monthSummaries.reduce(
      (acc, summary) => acc + (summary?.total_loans ?? 0),
      0,
    ) === 0;
    // Un abono exitoso puede llegar como activada, contraoferta o importe alto.
    const isSuccessfulOutcome = has(OperationOutcome.ACTIVATED)
      || has(OperationOutcome.COUNTER_OFFER)
      || has(OperationOutcome.HIGH_AMOUNT);
    // Condiciones de cada evento sobre el CONJUNTO de outcomes recibidos.
    const eventConditions: Record<EventPepperOneAlertId, boolean> = {
      // Operación activada (no es la primera del PDV)
      // además de que no hay operaciones activas en el mes actual ni en el anterior.
      [EventPepperOneAlertId.OPERATION_ACTIVATED]:
        has(OperationOutcome.ACTIVATED)
        && currentNumberOfLoans === 0
        && previousNumberOfLoans === 0,
      // Operación denegada.
      [EventPepperOneAlertId.OPERATION_DENIED]: has(OperationOutcome.DENIED),
      // Primera operación abonada del PDV, sea cual sea el canal de abono
      // (activada, contraoferta o importe alto). Tiene la prioridad más alta.
      [EventPepperOneAlertId.OPERATION_FIRST]:
        isSuccessfulOutcome && isFirstOperation,
      // Operación contraoferta (Siempre Sí).
      [EventPepperOneAlertId.OPERATION_COUNTER_OFFER]: has(
        OperationOutcome.COUNTER_OFFER,
      ),
      // Operación de importe alto (Belender / > 4.000€).
      [EventPepperOneAlertId.OPERATION_HIGH_AMOUNT]: has(
        OperationOutcome.HIGH_AMOUNT,
      ),
      // Contraoferta rechazada por el cliente.
      [EventPepperOneAlertId.OPERATION_COUNTER_OFFER_REJECTED]: has(
        OperationOutcome.COUNTER_OFFER_REJECTED,
      ),
    };
    return resolveAlertByPriority(EVENT_PEPPER_ONE_ALERTS, eventConditions);
  }, [outcomes, monthSummaries, ready]);

  //    se recalcula cuando cambian las métricas o las operaciones.
  const flags = useMemo<AlertsFlags>(() => {
    // cojo el día de hoy
    const today = /* testData ? dayjs('2024-05-29') : */ dayjs();
    // calculo el día del mes
    const dayOfMonth = today.date();
    // monthSummaries viene ordenado: [0] mes actual, [1] anterior, …
    const current = /* testData ? monthSummaries[1] : */ monthSummaries[0];
    const previous = /* testData ? monthSummaries[2] : */ monthSummaries[1];
    // ultimos 6 meses sin contar con el actual. Si hay menos de 6 meses de
    // datos, coge los que haya.
    const last6 = monthSummaries.slice(1, 7);

    // Para cada mes se mira el total de financiaciones abonadas (no vale solo aprobadas).
    const currentLoans = current?.total_amount ?? 0;
    const currentNumberOfLoans = current?.total_loans ?? 0;
    // Si no hay datos de meses anteriores, se asume que no hubo financiaciones (0).
    const previousLoans = previous?.total_amount ?? 0;
    const previousNumberOfLoans = previous?.total_loans ?? 0;
    const daysInMonth = today.daysInMonth();

    // tendencia del mes
    const trendMonth = dayOfMonth > 0 ? (currentLoans / dayOfMonth) * daysInMonth : 0;
    // mayor volumen de los últimos 6 meses (excluyendo el actual)
    const hadActivityLast6M = last6.some((m) => (m?.total_amount ?? 0) > 0);
    // Estados aprobados y captura en el dashboard actual.
    const hasPendingApproved = operations.some(
      (op) => op.business_status === InitialStatus.Aprobada,
    );
    const hasPendingCapture = operations.some(
      (op) => op.business_status === InitialStatus.Captura,
    );
    const computedFlags: AlertsFlags = {
      // 1 — PDV inactivo mes actual y anterior, pero activo últimos 6 meses.
      isPdvInactive2M:
        dayOfMonth > 1
        && currentNumberOfLoans === 0
        && previousNumberOfLoans === 0
        && hadActivityLast6M,
      // 2 — PDV inactivo mes actual pero activo mes anterior
      isPdvInactive1M: dayOfMonth > 5 && currentNumberOfLoans === 0,

      // 3 — Hay operaciones aprobadas pendientes.
      hasPendingApproved,

      // 4 — Ritmo bajo vs mes anterior (< 70%).
      isLowPacePrevMonth:
        dayOfMonth > 7
        && trendMonth > 0
        && trendMonth < PERCENT_50 * previousLoans,

      // 5 — Buen ritmo vs mes anterior (≥ 80%).
      isGoodPace:
        dayOfMonth > 7
        && trendMonth > 0
        && trendMonth >= PERCENT_80 * previousLoans,

      // 6 — Ritmo cercano al mes anterior (50% - 80%).
      isNearPacePrevMonth:
        dayOfMonth > 7
        && trendMonth > 0
        && trendMonth < PERCENT_80 * previousLoans
        && trendMonth >= PERCENT_50 * previousLoans,

      // 7 — Hay capturas pendientes.
      hasPendingCapture,
    };
    return computedFlags;
  }, [monthSummaries, operations]);

  // PRIORIDAD: qué aviso se muestra (dos tipos).
  //    tipo 1 — evento de pepper one, si hay uno activo gana SIEMPRE.
  //    tipo 2 — métrico, el de mayor prioridad activo.
  const activeAlertId = useMemo<AlertId | null>(() => {
    // Flag que activa cada aviso métrico; el orden lo da el `priority` de STATUS_ALERTS.
    const alertFlags: Record<StatusAlertId, boolean> = {
      [StatusAlertId.PDV_INACTIVE_2M]: flags.isPdvInactive2M,
      [StatusAlertId.PDV_INACTIVE_1M]: flags.isPdvInactive1M,
      [StatusAlertId.PENDING_APPROVED]: flags.hasPendingApproved,
      [StatusAlertId.LOW_PACE_PREV_MONTH]: flags.isLowPacePrevMonth,
      [StatusAlertId.GOOD_PACE]: flags.isGoodPace,
      [StatusAlertId.IS_NEAR_PACE_PREV_MONTH]: flags.isNearPacePrevMonth,
      [StatusAlertId.PENDING_CAPTURE]: flags.hasPendingCapture,
    };

    const metricAlertId = ready
      ? resolveAlertByPriority<StatusAlertId>(STATUS_ALERTS, alertFlags)
      : null;

    return eventAlertId ?? metricAlertId;
  }, [eventAlertId, flags, ready]);

  // aviso sigue visible y es el mismo, no se reenvía el evento.
  useEffect(() => {
    const meta = activeAlertId
      ? ALERT_ANALYTICS_META[activeAlertId]
      : undefined;
    if (!meta) return;

    // Los avisos por evento (Pepper One) solo se reportan a taxonomía cuando el
    // resultado ha llegado por postMessage(operación nueva).
    // Los avisos métricos no dependen de esto y se reportan siempre que cambian.
    const isEventAlertActive = !!activeAlertId && activeAlertId in EVENT_PEPPER_ONE_ALERTS;
    if (isEventAlertActive && !isFreshOutcomeRef.current) return;
    dispatch(refreshRecentMonthSummary() as unknown as AnyAction);
    registerShowNotificationBanner({
      notification_date: dayjs().format('YYYY-MM-DD'),
      notification_message: meta.message,
      notification_advice: meta.advice,
    });

    // Ya envíado a taxonomías siendo el mismo no
    // se vuelve a enviar hasta que llegue un nuevo postMessage.
    if (isEventAlertActive) isFreshOutcomeRef.current = false;
  }, [activeAlertId]);

  // 3) HANDLERS DE BOTÓN
  const handlers = useMemo<StatusAlertActionHandlers>(
    () => ({
      [StatusAlertActionId.NEW_OPERATION]: () => {
        registerClickNotificationBannerCta();
        triggerNewOperation();
      },
      [StatusAlertActionId.FILTER_APPROVED]: () => {
        registerClickNotificationBannerCta();
        selectOnlyStatus(InitialStatus.Aprobada);
      },
      [StatusAlertActionId.FILTER_CAPTURE]: () => {
        registerClickNotificationBannerCta();
        selectOnlyStatus(InitialStatus.Captura);
      },
      [StatusAlertActionId.CONTACT]: () => {
        registerClickNotificationBannerCta();
        goToContact();
      },
    }),
    [triggerNewOperation, selectOnlyStatus, goToContact],
  );

  return {
    handlers,
    flags,
    activeAlertId,
    isEventAlert: eventAlertId !== null,
    clearOperationOutcome,
    amount,
  };
}
